/* eslint semi: 1 */

import { t } from '@/i18n/i18ns';

import { asyncFormValidation, asyncValidationRule } from '@/element-ui/form-utils';
import { proxyException, PageResponse, EitherResponse } from '../player-setup-communication';
import OnEnterMixin from '../../../../mixins/onenter-mixin';
import { useMixpanel } from '@base-shared/composables/use-mixpanel';

import PlayerSetupFormError from '../player-setup-form-error/player-setup-form-error-tmpl';

const cleanupPin = (value) => value.replace(/[^\d]*/g, '');

export const validatePin = asyncValidationRule(async (rule, value) => {
  if (!value || value.trim() === '') {
    return new Error(t('label.pleaseEnterThePin'));
  }
  const cleanPin = cleanupPin(value);
  if (cleanPin !== value.trim()) {
    return new Error(t('label.pleaseEnterValidPin'));
  }

  // Check if the cleaned PIN length is between 4 and 6 digits
  if (cleanPin.length < 4 || cleanPin.length > 6) {
    return new Error(t('label.pinMustBeFourDigitNumber'));
  } else if (Number.parseInt(cleanPin) < 1000) {
    return new Error(t('label.pinMustBeAtleastOneThousand'));
  }
});

export default {
  name: 'player-setup-pin',
  inject: [
    '$utils',
    '$api'
  ],
  components: {
    PlayerSetupFormError
  },
  props: {
    nextPage: {
      type: [String, Object, null],
      default: null
    },
    isActive: {
      type: Boolean,
      default: false
    },
    serial: {
      type: [String, null],
      default: null
    }
  },
  data () {
    return {
      isFormDisabled: false,
      formError: '',
      model: {
        serial: '',
        pin: ''
      },
      modelRules: {
        serial: [{ required: true }],
        pin: [
          { required: true, message: t('label.pleaseEnterThePin'), trigger: 'change' },
          { validator: validatePin }
        ]
      }
    };
  },
  mixins: [OnEnterMixin],
  watch: {
    serial: {
      immediate: true,
      handler (val) {
        if (val) {
          this.model.serial = val;
        }
      }
    },
    isActive: {
      immediate: true,
      handler (val) {
        if (val) {
          this.refreshForm();
        }
      }
    }
  },
  mounted () {
    if (this.$refs.pinInput) {
      this.$refs.pinInput.focus();
    }
  },
  methods: {
    /**
     * Remove validation results from the screen and focus primary input
     */
    refreshForm () {
      this.formError = '';
      if (this.$refs.refFormPin) {
        this.$refs.refFormPin.clearValidate();
      }
      if (this.$refs.pinInput) {
        this.$refs.pinInput.focus();
      }
    },
    /**
     * Do the HTTP work and validation of the response
     * @param {string} serial
     * @param {string} pin
     * @returns EitherResponse (ok/error keys)
     */
    async verifyPIN (serial, pin) {
      const url = `${CONFIG.API_BASE_URL}/player`;
      const requestData = {
        pin,
        serialNumber: serial
      };

      const { ok: response, error } = await proxyException(() => this.$api.post(url, requestData));

      if (error) {
        let errorMessage = error.response?.data?.message;

        if (!error.response) {
          errorMessage = error;
        } else if (error.response.data && error.response.data.name === 'ExceededMaximumPlayersError') {
          errorMessage = error.response.data.description;
        }

        return EitherResponse(null, errorMessage);
      }

      useMixpanel().track('Player added');
      return EitherResponse(response.data);
    },
    toggleFormState (idDisabled) {
      this.isFormDisabled = idDisabled;
    },
    toggleNetworkLoader (isEnabled) {
      this.$utils.mainLoader(isEnabled);
    },

    /**
     * This is pattern to perform form validation and easily to test it
     */
    async validateForm (validateFn = asyncFormValidation) {
      this.toggleFormState(true);
      const isFormValid = await validateFn(this.$refs.refFormPin);
      this.toggleFormState(false);
      return isFormValid;
    },
    async submitForm () {
      const isFormValid = await this.validateForm();

      if (!isFormValid) {
        this.$refs.pinInput.focus();
        return PageResponse(true);
      }

      // FIXME: we would want to convert PIN to a string on the Backend
      const pin = parseInt(cleanupPin(this.model.pin));

      this.toggleNetworkLoader(true);
      const { ok: response, error } = await this.verifyPIN(this.serial, pin);
      this.toggleNetworkLoader(false);

      if (error) {
        this.refreshForm();
        this.formError = error;
        return PageResponse(true);
      }

      return PageResponse(false, this.nextPage, response);
    },
    async triggerFormSubmit (event) {
      this.$emit('SubmitForm');
      return false;
    },
    onEnter (event) {
      if (event.target.id === 'player-setup-pin') {
        this.triggerFormSubmit();
      }
    }
  }
};
